import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, seedAPI, recordsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { cn, formatDateTime, formatDate, getRecordTypeLabel } from '../lib/utils';
import FacilityTypeSelector from '../components/FacilityTypeSelector';
import ExpiryForecastDialog from '../components/ExpiryForecastDialog';
import {
  ClipboardCheck,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Users,
  TrendingUp,
  Calendar as CalendarIcon,
  Search,
  ArrowRight,
  Loader2,
  AlertCircle,
  Cylinder,
  Plus,
  FileText,
  RefreshCw,
  ChevronDown,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// Custom tooltip for chart
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const { user, isAdmin, isInspector, isRegulator } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [highRiskCylinders, setHighRiskCylinders] = useState({ cylinders: [], total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  
  // Quick Verify (Admin only)
  const [verifyQuery, setVerifyQuery] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  
  // New Inspection - Facility Type Selector (Inspector only)
  const [facilityTypeSelectorOpen, setFacilityTypeSelectorOpen] = useState(false);
  
  // Renew Inspection Type Selection Dialog (Inspector only)
  const [renewTypeDialogOpen, setRenewTypeDialogOpen] = useState(false);
  
  // Expiry Forecast filtered list dialog (Admin only)
  const [expiryDialog, setExpiryDialog] = useState({ open: false, window: null });
  
  // Filters
  const [recordType, setRecordType] = useState(searchParams.get('record_type') || 'all');
  const [dateRange, setDateRange] = useState(searchParams.get('days') || 'all');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });

  // Handle facility type selection for new inspection
  const handleFacilityTypeSelect = (type) => {
    navigate(`/inspections/new?facility_type=${type}`);
  };

  useEffect(() => {
    loadData();
  }, [recordType, dateRange, customDateRange, isAdmin, isInspector]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (recordType !== 'all') params.record_type = recordType;
      if (dateRange === 'custom' && customDateRange.from) {
        params.start_date = customDateRange.from.toISOString();
        if (customDateRange.to) params.end_date = customDateRange.to.toISOString();
      } else if (dateRange !== 'all' && dateRange !== 'custom') {
        params.days = parseInt(dateRange);
      }

      // Load stats for all users
      const statsRes = await dashboardAPI.getStats(params);
      setStats(statsRes.data);

      // Load trend data for both admin and inspector
      try {
        const trendDays = dateRange === 'all' ? 365 : (parseInt(dateRange) || 30);
        const trendRes = await dashboardAPI.getTrendByType(
          trendDays, 
          recordType !== 'all' ? recordType : null
        );
        setTrendData(trendRes.data);
      } catch (err) {
        // Fallback to regular trend if trend-by-type not available
        try {
          const trendDays = dateRange === 'all' ? 365 : (parseInt(dateRange) || 30);
          const trendRes = await dashboardAPI.getTrend(trendDays);
          setTrendData(trendRes.data);
        } catch (e) {
          console.log('Trend data not available');
        }
      }

      // High-risk cylinders (Admin and Regulator)
      if (isAdmin || isRegulator) {
        try {
          const highRiskRes = await dashboardAPI.getHighRiskCylinders(5);
          setHighRiskCylinders(highRiskRes.data);
        } catch (err) {
          console.log('High risk cylinders not available');
        }
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedAPI.seed();
      toast.success('Demo data created! Refreshing...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      if (err.response?.data?.message?.includes('already seeded')) {
        toast.info('Database already has demo data');
      } else {
        toast.error('Failed to seed database');
      }
    } finally {
      setSeeding(false);
    }
  };

  const handleQuickVerify = async () => {
    if (!verifyQuery.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await dashboardAPI.quickVerify(verifyQuery);
      setVerifyResult(res.data);
      if (res.data.length === 0) {
        toast.info('No results found');
      }
    } catch (err) {
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Navigate to recertification page with selected type
  const handleRenewalTypeSelect = (type) => {
    setRenewTypeDialogOpen(false);
    navigate(`/inspections/recertify?type=${type}`);
  };

  const handleKPIClick = (filterStatus) => {
    window.location.href = `/inspections?status=${filterStatus}`;
  };

  const getStatusBadge = (status, daysRemaining = null) => {
    if (status === 'valid' && daysRemaining !== null && daysRemaining <= 30) {
      return (
        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
          Expiring Soon
        </Badge>
      );
    }
    const styles = {
      valid: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      expired: 'bg-red-100 text-red-700 border-red-300',
      failed: 'bg-red-100 text-red-700 border-red-300',
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
    };
    return (
      <Badge variant="outline" className={styles[status] || 'bg-slate-100 text-slate-700'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'}
      </Badge>
    );
  };

  // KPI Cards - Same for both Admin and Inspector
  const kpiCards = [
    { 
      key: 'total',
      label: 'Total Inspections', 
      value: stats?.total_inspections || 0, 
      icon: ClipboardCheck, 
      color: 'text-slate-600',
      bgColor: 'bg-slate-100',
      onClick: () => handleKPIClick('all')
    },
    { 
      key: 'valid',
      label: 'Valid', 
      value: stats?.valid_inspections || 0, 
      icon: CheckCircle, 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      borderClass: 'border-l-4 border-l-emerald-500',
      onClick: () => handleKPIClick('valid')
    },
    { 
      key: 'expired',
      label: 'Expired', 
      value: stats?.expired_inspections || 0, 
      icon: XCircle, 
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderClass: 'border-l-4 border-l-red-500',
      onClick: () => handleKPIClick('expired')
    },
    { 
      key: 'pending',
      label: 'Pending', 
      value: stats?.pending_inspections || 0, 
      icon: Clock, 
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      borderClass: 'border-l-4 border-l-amber-500',
      onClick: () => handleKPIClick('pending')
    },
  ];

  // Admin-specific cards
  const adminExtraCards = [
    { 
      key: 'failed',
      label: 'Failed', 
      value: stats?.failed_inspections || 0, 
      icon: AlertTriangle, 
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      borderClass: 'border-l-4 border-l-red-400',
      onClick: () => handleKPIClick('failed')
    },
  ];

  // Inspector-specific cards (My Drafts)
  const inspectorExtraCards = [
    {
      key: 'drafts',
      label: 'My Drafts',
      value: stats?.draft_inspections || 0,
      icon: FileText,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      borderClass: 'border-l-4 border-l-indigo-500',
      onClick: () => handleKPIClick('draft'),
      testId: 'kpi-drafts',
    },
  ];

  const allKpiCards = isAdmin
    ? [...kpiCards, ...adminExtraCards]
    : isInspector
      ? [...kpiCards, ...inspectorExtraCards]
      : kpiCards;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Sticky Header with Filters */}
      <div className="sticky top-0 z-10 bg-background pb-4 border-b">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Inspection analytics and overview
            </p>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Vehicle Type Filter */}
            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger className="w-[160px]" data-testid="record-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="car">🚗 Light Vehicle</SelectItem>
                <SelectItem value="3w">🛺 3-Wheeler</SelectItem>
                <SelectItem value="bus">🚌 Bus</SelectItem>
                <SelectItem value="truck">🛻 Truck</SelectItem>
                <SelectItem value="trailer">🚛 Trailer</SelectItem>
                <SelectItem value="cng_storage">🏭 Storage</SelectItem>
              </SelectContent>
            </Select>

            {/* Time Range Chips */}
            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
              {[
                { value: 'all', label: 'All' },
                { value: '7', label: '7 Days' },
                { value: '30', label: '30 Days' },
                { value: '90', label: '90 Days' },
              ].map((chip) => (
                <button
                  key={chip.value}
                  onClick={() => setDateRange(chip.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                    dateRange === chip.value
                      ? "bg-white shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  data-testid={`date-range-${chip.value}`}
                >
                  {chip.label}
                </button>
              ))}
              
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1",
                      dateRange === 'custom'
                        ? "bg-white shadow-sm text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    data-testid="date-range-custom"
                  >
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Custom
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                      if (range?.from) setDateRange('custom');
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {(!stats?.total_inspections || stats.total_inspections === 0) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSeed}
                disabled={seeding}
                data-testid="seed-btn"
              >
                {seeding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Load Demo Data
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={cn(
        "grid gap-4",
        isAdmin ? "grid-cols-2 md:grid-cols-5" : "grid-cols-2 md:grid-cols-4"
      )}>
        {allKpiCards.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card 
              key={kpi.key} 
              className={cn(
                "cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5",
                kpi.borderClass
              )}
              onClick={kpi.onClick}
              data-testid={`kpi-${kpi.key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", kpi.bgColor)}>
                    <Icon className={cn("h-5 w-5", kpi.color)} />
                  </div>
                  <span className="text-2xl font-bold">{kpi.value.toLocaleString()}</span>
                </div>
                <p className="text-sm text-muted-foreground">{kpi.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Inspector: Add Inspection at TOP */}
        {isInspector && (
          <Card className="lg:col-span-12">
            <CardContent className="py-6">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <Plus className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Add Inspection</h3>
                <p className="text-muted-foreground text-sm mb-6 max-w-md">
                  Create a new inspection for a vehicle or CNG storage facility
                </p>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-inspection-btn">
                      <Plus className="mr-2 h-5 w-5" />
                      Add Inspection
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-56">
                    <DropdownMenuItem 
                      onClick={() => setFacilityTypeSelectorOpen(true)}
                      className="py-3"
                      data-testid="new-inspection-btn"
                    >
                      <FileText className="mr-3 h-5 w-5 text-emerald-600" />
                      <div>
                        <p className="font-medium">New Inspection</p>
                        <p className="text-xs text-muted-foreground">First-time vehicle/storage</p>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setRenewTypeDialogOpen(true)}
                      className="py-3"
                      data-testid="renew-inspection-btn"
                    >
                      <RefreshCw className="mr-3 h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium">Renew Inspection</p>
                        <p className="text-xs text-muted-foreground">Existing vehicle/storage</p>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => navigate('/inspections/bulk-upload')}
                      className="py-3"
                      data-testid="bulk-upload-btn"
                    >
                      <Upload className="mr-3 h-5 w-5 text-purple-600" />
                      <div>
                        <p className="font-medium">Bulk Upload</p>
                        <p className="text-xs text-muted-foreground">Upload multiple inspections</p>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inspector: Full-width Inspection Trends */}
        {isInspector && (
          <Card className="lg:col-span-12">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Inspection Trends
              </CardTitle>
              <CardDescription>
                Inspection volume over {dateRange === 'all' ? 'all time' : dateRange === 'custom' ? 'the selected period' : `the last ${dateRange} days`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trendData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  <div className="text-center">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No trend data available</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ fontSize: 12 }}
                      labelFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString();
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="Total Inspections"
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="expired" 
                      stroke="#94a3b8" 
                      strokeWidth={2}
                      name="Expired"
                      dot={{ fill: '#94a3b8', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="valid" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="Valid"
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Expiry Forecast - Admin only */}
        {isAdmin && (
        <Card className="lg:col-span-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Expiry Forecast
            </CardTitle>
            <CardDescription>Click any card to view details, export, or notify customers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { window: 'expired', count: stats?.expired_inspections || 0, dot: 'bg-red-600', tone: 'ring-red-300 hover:bg-red-50', badgeClass: 'bg-red-600 hover:bg-red-600 text-white', label: 'Already Expired' },
              { window: '7', count: stats?.expiring_7_days || 0, dot: 'bg-red-500', tone: 'ring-red-200 hover:bg-red-50', badgeClass: '', label: 'Within 7 days' },
              { window: '14', count: stats?.expiring_14_days || 0, dot: 'bg-amber-500', tone: 'ring-amber-200 hover:bg-amber-50', badgeClass: '', label: 'Within 14 days' },
              { window: '30', count: stats?.expiring_30_days || 0, dot: 'bg-yellow-500', tone: 'ring-yellow-200 hover:bg-yellow-50', badgeClass: '', label: 'Within 30 days' },
            ].map((item) => (
              <button
                type="button"
                key={item.window}
                onClick={() => setExpiryDialog({ open: true, window: item.window })}
                className={cn(
                  "w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg transition-colors cursor-pointer hover:ring-1 focus:outline-none focus:ring-2",
                  item.tone
                )}
                data-testid={`expiry-forecast-${item.window}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("h-2 w-2 rounded-full", item.dot)} />
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
                <Badge variant={item.window === 'expired' ? 'default' : 'secondary'} className={item.badgeClass}>{item.count}</Badge>
              </button>
            ))}
          </CardContent>
        </Card>
        )}

        {/* Inspection Trends Chart - Admin (8-col) / Regulator (full-width) */}
        {isAdmin && (
        <Card className="lg:col-span-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Inspection Trends
            </CardTitle>
            <CardDescription>
              Inspection volume over {dateRange === 'all' ? 'all time' : dateRange === 'custom' ? 'the selected period' : `the last ${dateRange} days`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length === 0 ? (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No trend data available</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return format(date, 'MMM d');
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                    formatter={(value) => {
                      const labels = {
                        total: 'Total Inspections',
                        valid: 'Valid Inspections',
                        new: 'New Inspections',
                        periodic: 'Periodic',
                        after_accident: 'After Accident',
                      };
                      return labels[value] || value;
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#94a3b8" 
                    strokeWidth={2}
                    dot={{ fill: '#94a3b8', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="valid" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        )}

        {/* Bottom Section - Different for Admin vs Regulator (Inspector handled above) */}
        {isAdmin ? (
          <>
            {/* High-Risk Cylinders (Admin) */}
            <Card className="lg:col-span-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      High-Risk Cylinders
                    </CardTitle>
                    <CardDescription>
                      Cylinders expiring within 30 days or already expired
                    </CardDescription>
                  </div>
                  {highRiskCylinders.total_count > 5 && (
                    <Badge variant="destructive">
                      {highRiskCylinders.total_count} total
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {highRiskCylinders.cylinders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Cylinder className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No high-risk cylinders found</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Cylinder Serial</TableHead>
                          <TableHead className="text-right">Expiry</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {highRiskCylinders.cylinders.map((cyl, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/50">
                            <TableCell className="font-mono text-sm">
                              {cyl.registration_number || cyl.storage_serial_number || '-'}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {cyl.cylinder_serial}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge 
                                variant="outline"
                                className={cn(
                                  cyl.days_remaining <= 0 
                                    ? "bg-red-100 text-red-700 border-red-300" 
                                    : cyl.days_remaining <= 7
                                    ? "bg-red-50 text-red-600 border-red-200"
                                    : "bg-amber-100 text-amber-700 border-amber-300"
                                )}
                              >
                                {cyl.days_remaining <= 0 
                                  ? `${Math.abs(cyl.days_remaining)}d overdue` 
                                  : `${cyl.days_remaining}d left`}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {highRiskCylinders.total_count > 5 && (
                      <div className="mt-4 text-center">
                        <Button variant="ghost" asChild size="sm">
                          <Link to="/records?cylinder_status=expired">
                            View All {highRiskCylinders.total_count} Cylinders →
                          </Link>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Verify (Admin) */}
            <Card className="lg:col-span-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  Quick Verify
                </CardTitle>
                <CardDescription>
                  Search by registration, cylinder serial, or verification number
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter search term..."
                    value={verifyQuery}
                    onChange={(e) => setVerifyQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleQuickVerify()}
                    data-testid="quick-verify-input"
                  />
                  <Button onClick={handleQuickVerify} disabled={verifying} data-testid="quick-verify-btn">
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                
                {verifyResult && verifyResult.length > 0 && (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {verifyResult.map((result, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono font-medium text-sm">
                            {result.record?.registration_number || 
                             result.record?.storage_serial_number || 
                             result.cylinder?.serial_number ||
                             result.inspection?.verification_number}
                          </span>
                          {getStatusBadge(result.inspection?.status)}
                        </div>
                        {result.inspection && (
                          <div className="text-xs text-muted-foreground space-y-1">
                            <p>Valid until: {result.inspection.valid_to ? formatDate(result.inspection.valid_to) : 'N/A'}</p>
                            <p>Inspector: {result.inspection.inspector_name || 'N/A'}</p>
                          </div>
                        )}
                        {result.inspection && (
                          <Link 
                            to={`/inspections?search=${result.inspection.verification_number}`}
                            className="text-xs text-primary hover:underline mt-2 inline-block"
                          >
                            View Details →
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {verifyResult && verifyResult.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No results found</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Approvals Alert (Admin) */}
            {stats?.pending_approvals > 0 && (
              <Card className="lg:col-span-12 border-amber-200 bg-amber-50/50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">Pending User Approvals</p>
                        <p className="text-sm text-muted-foreground">
                          {stats.pending_approvals} user(s) waiting for approval
                        </p>
                      </div>
                    </div>
                    <Button asChild>
                      <Link to="/admin/users?status=pending_approval">
                        Review Now <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : isRegulator ? (
          /* Regulator Section - Full-width Inspection Trends + High-Risk Cylinders + Quick Verify */
          <>
            {/* Full-width Inspection Trends for Regulator */}
            <Card className="lg:col-span-12">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Inspection Trends
                </CardTitle>
                <CardDescription>
                  Inspection volume over {dateRange === 'all' ? 'all time' : dateRange === 'custom' ? 'the selected period' : `the last ${dateRange} days`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {trendData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    <div className="text-center">
                      <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No trend data available</p>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getDate()}/${date.getMonth() + 1}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ fontSize: 12 }}
                        labelFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString();
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        name="Total Inspections"
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="expired" 
                        stroke="#94a3b8" 
                        strokeWidth={2}
                        name="Expired"
                        dot={{ fill: '#94a3b8', strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="valid" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        name="Valid"
                        dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* High-Risk Cylinders (Regulator) */}
            <Card className="lg:col-span-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      High-Risk Cylinders
                    </CardTitle>
                    <CardDescription>
                      Cylinders expiring within 30 days or already expired
                    </CardDescription>
                  </div>
                  {highRiskCylinders.total_count > 5 && (
                    <Badge variant="destructive">
                      {highRiskCylinders.total_count} total
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {highRiskCylinders.cylinders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Cylinder className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No high-risk cylinders found</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vehicle/Facility</TableHead>
                          <TableHead>Cylinder Serial</TableHead>
                          <TableHead className="text-right">Expiry</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {highRiskCylinders.cylinders.map((cyl, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/50">
                            <TableCell className="font-mono text-sm">
                              {cyl.registration_number || cyl.storage_serial_number || '-'}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {cyl.cylinder_serial}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge 
                                variant="outline"
                                className={cn(
                                  cyl.days_remaining <= 0 
                                    ? "bg-red-100 text-red-700 border-red-300" 
                                    : cyl.days_remaining <= 7
                                    ? "bg-red-50 text-red-600 border-red-200"
                                    : "bg-amber-100 text-amber-700 border-amber-300"
                                )}
                              >
                                {cyl.days_remaining <= 0 
                                  ? `${Math.abs(cyl.days_remaining)}d overdue` 
                                  : `${cyl.days_remaining}d left`}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {highRiskCylinders.total_count > 5 && (
                      <div className="mt-4 text-center">
                        <Button variant="ghost" asChild size="sm">
                          <Link to="/records?cylinder_status=expired">
                            View All {highRiskCylinders.total_count} Cylinders →
                          </Link>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Verify (Regulator) */}
            <Card className="lg:col-span-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  Quick Verify
                </CardTitle>
                <CardDescription>
                  Search by registration, cylinder serial, or verification number
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter search term..."
                    value={verifyQuery}
                    onChange={(e) => setVerifyQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleQuickVerify()}
                    data-testid="regulator-quick-verify-input"
                  />
                  <Button onClick={handleQuickVerify} disabled={verifying} data-testid="regulator-quick-verify-btn">
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                
                {verifyResult && verifyResult.length > 0 && (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {verifyResult.map((result, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono font-medium text-sm">
                            {result.record?.registration_number || 
                             result.record?.storage_serial_number || 
                             result.cylinder?.serial_number ||
                             result.inspection?.verification_number}
                          </span>
                          {getStatusBadge(result.inspection?.status)}
                        </div>
                        {result.inspection && (
                          <div className="text-xs text-muted-foreground space-y-1">
                            <p>Valid until: {result.inspection.valid_to ? formatDate(result.inspection.valid_to) : 'N/A'}</p>
                            <p>Inspector: {result.inspection.inspector_name || 'N/A'}</p>
                          </div>
                        )}
                        {result.inspection && (
                          <Link 
                            to={`/inspections?search=${result.inspection.verification_number}`}
                            className="text-xs text-primary hover:underline mt-2 inline-block"
                          >
                            View Details →
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {verifyResult && verifyResult.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No results found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* Facility Type Selector Dialog (Inspector) */}
      <FacilityTypeSelector
        open={facilityTypeSelectorOpen}
        onOpenChange={setFacilityTypeSelectorOpen}
        onSelect={handleFacilityTypeSelect}
      />

      {/* Expiry Forecast Filtered List Dialog (Admin) */}
      <ExpiryForecastDialog
        open={expiryDialog.open}
        window={expiryDialog.window}
        onClose={() => setExpiryDialog({ open: false, window: null })}
      />

      {/* Renewal Type Selection Dialog (Inspector) */}
      <Dialog open={renewTypeDialogOpen} onOpenChange={setRenewTypeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Renewal Type</DialogTitle>
            <DialogDescription>
              Choose the type of inspection renewal to perform
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div
              className="flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 hover:border-primary transition-all"
              onClick={() => handleRenewalTypeSelect('periodic')}
              data-testid="renewal-type-periodic"
            >
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">Periodic Inspection</p>
                <p className="text-sm text-muted-foreground">Regular scheduled renewal</p>
              </div>
            </div>
            <div
              className="flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 hover:border-primary transition-all"
              onClick={() => handleRenewalTypeSelect('after_accident')}
              data-testid="renewal-type-accident"
            >
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold">After Accident / Repair</p>
                <p className="text-sm text-muted-foreground">Post-incident re-certification</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
